import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/apiConfig';
import { APP_CONFIG } from '../config/appConfig';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** When true, failed responses skip the global toast (caller shows its own message). */
    skipGlobalErrorToast?: boolean;
  }
}

function normalizeApiErrorMessage(data: unknown): string {
  if (data == null || typeof data !== 'object') {
    return '';
  }
  const msg = (data as { message?: unknown }).message;
  if (Array.isArray(msg)) {
    return msg.filter((m) => typeof m === 'string').join(' ');
  }
  if (typeof msg === 'string') {
    return msg;
  }
  return '';
}

// Create axios instance with default timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: APP_CONFIG.api.timeout, // Default timeout, will be updated by settings
});

// Function to update API timeout dynamically
export const updateApiTimeout = (timeout: number) => {
  api.defaults.timeout = timeout;
};

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Instance defaults to application/json; FormData must omit Content-Type so the runtime sets
    // multipart/form-data with the correct boundary (otherwise Multer sees no file field).
    if (config.data instanceof FormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete (config.headers as Record<string, unknown>)['Content-Type'];
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const message =
      normalizeApiErrorMessage(error.response?.data) ||
      error.message ||
      'An error occurred';

    // Handle 401 Unauthorized
    if (status === 401) {
      localStorage.removeItem('access_token');
      // Don't redirect on public pages (e.g. register page fetches invitation without auth; other providers may fetch and get 401)
      const isPublicPage =
        window.location.pathname === '/login' ||
        window.location.pathname === '/register' ||
        window.location.pathname.startsWith('/rfq/');
      if (!isPublicPage) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle 403 Forbidden
    if (status === 403) {
      console.error('Forbidden:', message);
      toast.error(message || 'You don\'t have permission to perform this action.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 404 Not Found
    if (status === 404) {
      console.error('Not Found:', message);
      toast.error(message || 'Resource not found.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 500+ Server Errors
    if (status && status >= 500) {
      console.error('Server Error:', message);
      toast.error(message || 'Server error. Please try again later.', { duration: 5000 });
      return Promise.reject(error);
    }

    // Handle 400 Bad Request (validation, business rules, etc.)
    if (status === 400) {
      const reqUrl = String(error.config?.url ?? '');
      // Firefox DevTools / bad SPA links can trigger /crew/<non-numeric> → ParseIntPipe noise
      const isCrewIdParseNoise =
        /numeric string is expected/i.test(message) &&
        /\/crew\/(?!lookups|reports|replacements|travels|vessel-list|certs|medicals|rank-requirements|officer-matrix|rightship-matrix|changes-per-vessel)[^/]+/.test(
          reqUrl,
        ) &&
        !/\/crew\/\d+(\/|$|\?)/.test(reqUrl);
      if (isCrewIdParseNoise) {
        return Promise.reject(error);
      }
      console.error('Bad Request:', message);
      if (!error.config?.skipGlobalErrorToast) {
        toast.error(message || 'Request could not be completed.', { duration: 5000 });
      }
      return Promise.reject(error);
    }

    // Default error handling
    console.error('API Error:', message);
    toast.error(message || 'An error occurred.', { duration: 5000 });
    return Promise.reject(error);
  }
);

// Export the instance
export default api;
